import { useCallback } from "react";
import trpc from "@/utils/trpc";
import { Button } from "@/components/button/button";
import { CheckCircleIcon as CheckCircleIconOutline } from "@heroicons/react/24/outline";
import { CheckCircleIcon as CheckCircleIconSolid } from "@heroicons/react/24/solid";
import useCompletedByItemId from "@/hooks/use-completed-by-item-id";
import useCurrentModuleItemId from "@/hooks/use-current-module-item-id";
import useShouldShowMarkAsDone from "@/hooks/use-should-show-mark-as-done";

/**
 * Renders a "Mark as done" button if the current module item requires manual completion
 */
export default function MarkAsDone() {
  const currentModuleItemId = useCurrentModuleItemId();
  const shouldShow = useShouldShowMarkAsDone();

  const { isCompleted: isCompletedFn, isProgressionPending, isProgressionFetching } = useCompletedByItemId();
  const upsertProgressionMutation = trpc.progression.upsert.useMutation();
  const utils = trpc.useUtils();

  const isCompleted = isCompletedFn(currentModuleItemId);

  const onToggleDone = useCallback(async () => {
    try {
      if (isCompleted) {
        await upsertProgressionMutation.mutateAsync({ itemId: String(currentModuleItemId), completed: false });
      } else {
        await upsertProgressionMutation.mutateAsync({ itemId: String(currentModuleItemId), completed: true });
      }
      // Invalidate progression to trigger a refetch and update completion state
      await utils.progression.get.invalidate();
    } catch (e) {
      console.error(e);
    }
  }, [currentModuleItemId, isCompleted, upsertProgressionMutation, utils]);

  if (!shouldShow) return null;

  return (
    <div className="flex justify-end">
      <Button
        onClick={onToggleDone}
        loading={upsertProgressionMutation.isPending || isProgressionPending || isProgressionFetching}
        filled={isCompleted ? "tint" : "lac-leman"}
        className="flex items-center justify-center gap-2 px-5 py-2.5">
        <span className="flex items-center gap-2">
          {isCompleted ? "Marked as done" : "Mark as done"}
          {isCompleted ? <CheckCircleIconSolid className="h-5 w-5" /> : <CheckCircleIconOutline className="h-5 w-5" />}
        </span>
      </Button>
    </div>
  );
}
